'use client';

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { homeAssetSrc } from 'components/landing/v3/homeAssetSrc';
import { ringProUsAsset, ringProUsCdnAsset } from '../ringProUsAsset';
import styles from './SpotlightGallery.module.css';

/** Full CDN URL for <video>. Path must start with /. */
const cdn = (path: string) => getCompressedAssetUrl(homeAssetSrc(path));
const img = (file: string) => ringProUsAsset(`spotlight/${file}`);

type Slide = {
  id: string;
  image: string;
  video?: string;
  poster?: string;
  /* Caption at the top when the subject sits low in the frame. */
  captionTop?: boolean;
};

/* Carousel stills (Figma 2822:2346), 712 x 520 frames at 4×. UltraSnap
   keeps its film; the poster is the new still. */
const SLIDES: Slide[] = [
  {
    id: 'titanium',
    video: cdn('/web_v2/home-v4/ring-pro-air/ring-pro.mp4'),
    poster: cdn('/web_v2/home-v4/video-posters/ring-pro/first-frame.jpg'),
    image: homeAssetSrc('web_v2/home-v4/video-posters/ring-pro/first-frame.jpg'),
  },
  {
    id: 'ultrasnap',
    video: cdn('/web_v2/ring-pro-us/videos/ultrasnap.mp4'),
    poster: ringProUsCdnAsset('spotlight/ultrasnap.png'),
    image: img('ultrasnap.png'),
    captionTop: true,
  },
  { id: 'water', image: img('swim.png') },
  { id: 'jade', image: img('jade.png') },
  { id: 'storage', image: img('hike.png') },
  { id: 'heart', image: img('heart.jpg') },
];

/**
 * Left column of the US Ring PRO spotlight (Figma 2535:10737): a 712 x 520
 * stage with one feature line over it and six 96 x 72 thumbnails that switch
 * it, faded at both ends. Thumbnails are a tablist (arrow keys move).
 */
export const SpotlightGallery = () => {
  const t = useTranslations('homepage');
  const [active, setActive] = useState(0);
  const thumbRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
  const k = (key: string) => `homepage.ringProUs.spotlight.gallery.${key}`;

  /* Only the visible film plays, and it restarts each time it comes back. */
  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === active) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [active]);

  const onKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const dir = event.key === 'ArrowRight' ? 1 : -1;
    const next = (index + dir + SLIDES.length) % SLIDES.length;
    setActive(next);
    thumbRefs.current[next]?.focus();
  };

  return (
    <div className={styles.root}>
      <div className={styles.stage}>
        {SLIDES.map((slide, i) => (
          <div
            key={slide.id}
            id={`ringpro-gallery-slide-${slide.id}`}
            role="tabpanel"
            aria-labelledby={`ringpro-gallery-thumb-${slide.id}`}
            aria-hidden={i !== active}
            className={styles.slide}
            data-active={i === active ? 'true' : undefined}
            data-caption={slide.captionTop ? 'top' : undefined}
          >
            {slide.video ? (
              <video
                ref={(el) => {
                  videoRefs.current[i] = el;
                }}
                className={styles.media}
                src={slide.video}
                poster={slide.poster}
                autoPlay={i === 0}
                muted
                loop
                playsInline
                preload={i === 0 ? 'auto' : 'metadata'}
                aria-label={t(k(`${slide.id}.alt`))}
              />
            ) : (
              <CustomImage
                className={styles.media}
                src={slide.image}
                alt={t(k(`${slide.id}.alt`))}
                fill
                sizes="(max-width: 767px) 100vw, 712px"
              />
            )}
            <p className={styles.caption}>
              {t.rich(k(`${slide.id}.caption`), {
                b: (chunks) => (
                  <strong className={styles.lead}>{chunks}</strong>
                ),
              })}
            </p>
          </div>
        ))}
      </div>

      <div
        className={styles.thumbs}
        role="tablist"
        aria-label={t(k('aria.thumbs'))}
      >
        {SLIDES.map((slide, i) => (
          <button
            key={slide.id}
            ref={(el) => {
              thumbRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`ringpro-gallery-thumb-${slide.id}`}
            aria-selected={i === active}
            aria-controls={`ringpro-gallery-slide-${slide.id}`}
            aria-label={t(k(`${slide.id}.alt`))}
            tabIndex={i === active ? 0 : -1}
            className={styles.thumb}
            onClick={() => setActive(i)}
            onKeyDown={(event) => onKey(event, i)}
          >
            <CustomImage src={slide.image} alt="" width={96} height={72} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default SpotlightGallery;
